import { Deserializer } from 'jsonapi-serializer';
import { stateTypeDirectTable, typeSystemStats } from '../constants/api';
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from '../constants/response-status-codes';
import { AccessGroup } from '../models/AccessGroup';
import { AccessGroupMaster } from '../models/AccessGroupMaster';
import { Filter } from '../models/Filter';
import { couldNotFindRecordToDelete, genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { Name } from '../models/Name';
import { PaginationModel } from '../models/Pagination';
import { DeleteEntityResult } from '../models/services/DeleteEntityResult';
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { AccessGroupWrapper } from '../models/wrappers/AccessGroupWrapper';
import { convertDateToNumber, convertNumberToDate } from '../utilities/DateUtils';
import { appendFilter } from '../utilities/filters';
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { APIConnector } from './api/APIConnector';
import { customFilterBuilder } from "./utilities/customFilterBuilder";
import { buildDayList, convertToDuration, convertToTime, getDays } from "./utilities/dateFormatting";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { Translator } from '../contexts/translator';
import { ACCESS_TYPES } from '../constants/access-constants';
import { GROUP_TYPE } from '@/constants/area-constants';

const apiConnector = new APIConnector();

const buildAccessGroup: (value: any, translate: Translator, profileNames: string | undefined) => AccessGroup = (apiAccessGroup, translate: Translator, profileNames?): AccessGroup => {
    const accessGroupActions = apiAccessGroup['access-group-actions'] || [];

    return {
        id: apiAccessGroup['id'],
        name: apiAccessGroup['ag-name'],
        agType: apiAccessGroup['ag-type'],
        accessTimePatterns: apiAccessGroup['access-time-pattern']['atp-name'],
        area: apiAccessGroup['area']['area-name'],
        site: apiAccessGroup['site']['site-name'],
        enrolments: apiAccessGroup['total-masters'],
        siteId: apiAccessGroup['site']['id'],
        accessTimePatternId: apiAccessGroup['access-time-pattern']['id'],
        startTime: apiAccessGroup['access-time-pattern']['atp-start-time'] ? convertToTime(apiAccessGroup['access-time-pattern']['atp-start-time']) : '--:--',
        duration: apiAccessGroup['access-time-pattern']['atp-duration'] ? convertToDuration(apiAccessGroup['access-time-pattern']['atp-duration']) : '24hh:mm',
        days: getDays(
            apiAccessGroup['access-time-pattern']['atp-mon'],
            apiAccessGroup['access-time-pattern']['atp-tue'], 
            apiAccessGroup['access-time-pattern']['atp-wed'], 
            apiAccessGroup['access-time-pattern']['atp-thu'], 
            apiAccessGroup['access-time-pattern']['atp-fri'], 
            apiAccessGroup['access-time-pattern']['atp-sat'], 
            apiAccessGroup['access-time-pattern']['atp-sun'], 
            apiAccessGroup['access-time-pattern']['atp-hol'],
            translate),
        dayList: buildDayList(
            apiAccessGroup['access-time-pattern']['atp-mon'],
            apiAccessGroup['access-time-pattern']['atp-tue'],
            apiAccessGroup['access-time-pattern']['atp-wed'],
            apiAccessGroup['access-time-pattern']['atp-thu'],
            apiAccessGroup['access-time-pattern']['atp-fri'],
            apiAccessGroup['access-time-pattern']['atp-sat'],
            apiAccessGroup['access-time-pattern']['atp-sun'],
            apiAccessGroup['access-time-pattern']['atp-hol'],
        ),
        accessAreaId: apiAccessGroup['area']['id'],
        endDate: apiAccessGroup['ag-end-date'] != 0 ? convertNumberToDate(apiAccessGroup['ag-end-date']) : undefined,
        maxCapacity: +apiAccessGroup['ag-max-capacity'],
        startDate: apiAccessGroup['ag-start-date'] != 0 ? convertNumberToDate(apiAccessGroup['ag-start-date']) : undefined,
        suspended: apiAccessGroup['ag-suspended'],
        entryActions: accessGroupActions?.filter(action => action && action['aga-direction'] === 0).length,
        exitActions: accessGroupActions?.filter(action => action && action['aga-direction'] === 1).length,
        description: apiAccessGroup['ag-desc'] ?? '',
        default: apiAccessGroup['ag-default'],
        isDefault: apiAccessGroup['is-default'],
        profiles: apiAccessGroup['total-access-profiles'],
        accessProfile: profileNames
    };
};

export const getAccessGroupById = async (id: string, translate: Translator): Promise<AccessGroup> => {
    try {
        const accessGroupFields = 'fields[accessGroup]=accessGroupActions,agName,agStartDate,agEndDate,agMaxCapacity,agSuspended,agDesc,agDefault,totalMasters,totalAccessProfiles,site,accessTimePattern,area';
        const accessGroupActionsFields = 'fields[accessGroupAction]=agaDirection';
        const areaFields = 'fields[area]=areaName';
        const siteFields = 'fields[site]=siteName';
        const atpFields = 'fields[accessTimePattern]=atpName,atpStartTime,atpDuration,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol';
        const url = `accessGroup/${id}?include=accessTimePattern,area,site,accessGroupActions&${accessGroupFields}&${accessGroupActionsFields}&${areaFields}&${siteFields}&${atpFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessGroup: any) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }
                const accessGroup = buildAccessGroup(apiAccessGroup, translate, apiAccessGroup['access-profile-names']);
                return resolve(accessGroup);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessGroupsForPerson = async (translate: Translator, listParameters?: ListQuery): Promise<AccessGroupWrapper> => {
    try {
        const searchOnNames: string[] = ['accessGroup.accessTimePattern.atpMon', 'accessGroup.accessTimePattern.atpTue', 'accessGroup.accessTimePattern.atpWed', 'accessGroup.accessTimePattern.atpThu', 'accessGroup.accessTimePattern.atpFri', 'accessGroup.accessTimePattern.atpSat', 'accessGroup.accessTimePattern.atpSun', 'accessTimePattern.atpHol'];
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, customFilterBuilder(searchOnNames, translate, listParameters?.filters, listParameters?.searchTerm));
        const masterGroupFields = 'fields[masterGroup]=accessGroup,accessProfileNames';
        const accessGroupFields = 'fields[accessGroup]=agName,agType,totalMasters,site,area,accessTimePattern';
        const atpFields = 'fields[accessTimePattern]=atpName,atpStartTime,atpDuration,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol';
        const areaFields = 'fields[area]=areaName';
        const siteFields = 'fields[site]=siteName';
        const url = `masterGroup?include=accessGroup,accessGroup.accessTimePattern,accessGroup.area,accessGroup.site${partialURL}&${masterGroupFields}&${accessGroupFields}&${atpFields}&${areaFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, masterGroups: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const accessGroups: AccessGroup[] = masterGroups.map((personAccessGroup) => buildAccessGroup(personAccessGroup['access-group'], translate, personAccessGroup['access-profile-names']));
                return resolve({ rows: accessGroups, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessGroupsNamesWithPagination = async (accessType: GROUP_TYPE = null, listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filtersWithAccessType = accessType !== null ? appendFilter(accessType, 'Type', 'agType', listQuery?.filters ?? []) : listQuery?.filters;
        const filters: string = getFilterClause(filtersWithAccessType, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const accessGroupFields = 'fields[accessGroup]=agName,isDefault,site,agType';
        const siteFields = 'fields[site]=siteName';
        const url = `accessGroup?${paginationString}&include=site&${accessGroupFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessGroups: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const accessGroups: Name[] = apiAccessGroups.map((accessGroup): Name => {
                    return {
                        value: accessGroup['id'],
                        label: accessGroup['ag-name'],
                        optionalColumns: [{ key: 'site', value: accessGroup['site']['site-name'] }],
                        isDefault: accessGroup['is-default']
                    };
                });

                return resolve({ rows: accessGroups, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAllAccessGroups = async (accessType: GROUP_TYPE = null, translate: Translator, listParameters?: ListQuery): Promise<AccessGroupWrapper> => {
    try {
        const searchOnNames: string[] = ['accessTimePattern.atpMon', 'accessTimePattern.atpTue', 'accessTimePattern.atpWed', 'accessTimePattern.atpThu', 'accessTimePattern.atpFri', 'accessTimePattern.atpSat', 'accessTimePattern.atpSun', 'accessTimePattern.atpHol'];
        const filters = accessType !== null ? appendFilter(accessType, 'Type', 'agType', listParameters?.filters ?? []) : listParameters?.filters;
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, customFilterBuilder(searchOnNames, translate, filters, listParameters?.searchTerm));
        const fieldsAccessGroup = 'fields[accessGroup]=site,accessTimePattern,area,accessProfileNames,agName,agStartDate,agEndDate,totalMasters,isDefault,agType';
        const fieldsAccessTimePattern = 'fields[accessTimePattern]=atpName,atpStartTime,atpDuration,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol';
        const fieldsArea = 'fields[area]=areaName';
        const fieldsSite = 'fields[site]=siteName';
        let url = `accessGroup?include=accessTimePattern,area,site${partialURL}&${fieldsAccessGroup}&${fieldsAccessTimePattern}&${fieldsArea}&${fieldsSite}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, accessGroup: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }
                
                const accessGroups: AccessGroup[] = accessGroup.map((accessGroup) => buildAccessGroup(accessGroup, translate, accessGroup['access-profile-names']));
                return resolve({ rows: accessGroups, pagination });
            })
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const storeAccessGroup = async (accessGroup: AccessGroup, createAccessGroup = true) => {
    try {
        const newRecord = {
            data: {
                type: 'accessGroup',
                id: accessGroup.id,
                attributes: {
                    agDefault: accessGroup.default ?? 0,
                    agName: accessGroup.name,
                    agSuspended: accessGroup.suspended,
                    agMaxCapacity: accessGroup.maxCapacity,
                    agStartDate: accessGroup.startDate ? convertDateToNumber(accessGroup.startDate) : 0,
                    agEndDate: accessGroup.endDate ? convertDateToNumber(accessGroup.endDate) : 0,
                    agAllowAccess: accessGroup.allowAccess ?? 1,
                    agArming: accessGroup.arming ?? 0,
                    agDesc: accessGroup.description,
                    agDisarming: accessGroup.disarming ?? 0,
                    // agNo: accessGroup.number ?? null,
                    agReport: accessGroup.report ?? 1,
                    agsGroupNo: accessGroup.sGroupNumber,
                    agType: accessGroup.agType ?? ACCESS_TYPES.ACCESS_GROUP,
                },
                relationships: {
                    area: {
                        data: {
                            type: 'area',
                            id: accessGroup.accessAreaId
                        }
                    },
                    accessTimePattern: {
                        data: {
                            type: 'accessTimePattern',
                            id: accessGroup.accessTimePatternId
                        }
                    },
                    site: {
                        data: {
                            type: 'site',
                            id: accessGroup.siteId
                        }
                    }
                }
            }
        };

        const url = `accessGroup${createAccessGroup ? '' : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector.sendData(url, createAccessGroup, newRecord)
            .then(data => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createAccessGroup) {
                    const content = await response.json();
                    if (content.errors) {
                        return reject({ status: response.status, message: content.errors[0].detail })
                    }
                    new Deserializer().deserialize(content, (error, newAccessGroup) => {
                        if (error) {
                            return reject({ status: response.status, id: accessGroup.id, success: false, message: genericError });
                        }
                        return resolve({ id: newAccessGroup.id, success: true });
                    })
                    return;
                }

                if (response.status === NO_CONTENT) {
                    return resolve({ id: accessGroup.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: accessGroup.id, success: false, message: content.errors.map(error => error.detail) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: accessGroup.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deleteAccessGroup = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `accessGroup/${id}`;
        const accessGroup = {
            data: {
                type: "accessGroup",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, accessGroup)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const assignUnassignMasterGroupRelationship = async (toBeAssigned: string[], toBeUnassigned: string[], accessGroupId: string) => {
    const newMasterGroups = buildToBeSavedRelationship(toBeAssigned, accessGroupId);

    const addedAccessGroups = newMasterGroups.map(async (newMasterGroup) => {
        return await saveRelationship(newMasterGroup, true)
    })

    const removedAccessGroups = toBeUnassigned.map(async (masterId) => {
        return await removeRelationship(masterId, accessGroupId)
    })

    return Promise.all([...addedAccessGroups, ...removedAccessGroups]);
}

const buildToBeSavedRelationship = (toBeAssigned: string[], accessGroupId: string) => {
    return toBeAssigned.map((masterId) => {
        return ({
            data: {
                type: 'masterGroup',
                id: "",
                attributes: {
                    mgDateTimeInEffect: null,
                    mgEndDateTimeUtc: null,
                    mgExpires: 0,
                    mgStartDateTimeUtc: null
                },
                relationships: {
                    master: {
                        data: {
                            type: 'master',
                            id: masterId
                        }
                    },
                    accessGroup: {
                        data: {
                            type: 'accessGroup',
                            id: accessGroupId
                        }
                    }
                }
            }
        })
    })
}

const buildToBeRemovedRelationships = (accessGroupRelationship: AccessGroupMaster) => {
    return {
        data: {
            type: 'masterGroup',
            id: accessGroupRelationship.id,
            attributes: {
                mgDateTimeInEffect: accessGroupRelationship.dateTimeEffect,
                mgEndDateTimeUtc: accessGroupRelationship.endDateTimeUtc,
                mgExpires: accessGroupRelationship.expires,
                mgStartDateTimeUtc: accessGroupRelationship.startDateTimeUtc
            },
            relationships: {
                master: {
                    data: {
                        type: 'master',
                        id: accessGroupRelationship.masterId
                    }
                },
                accessGroup: {
                    data: {
                        type: 'accessGroup',
                        id: accessGroupRelationship.accessGroupId
                    }
                }
            }
        }
    }
}

export const saveRelationship = async (relationship, isNewRelationship = true) => {
    try {
        const url = `masterGroup`;
        let response: Response;

        await apiConnector.sendData(url, isNewRelationship, relationship).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }
            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const removeRelationship = async (masterId: string, accessGroupId: string): Promise<boolean> => {
    try {
        // First lookup the Master Group
        const relationship = await getMasterGroupRelationship(masterId, accessGroupId);

        // Found the Master Group, let's delete it then.
        if (relationship && relationship.length > 0) {
            const relationshipId = relationship[0].id;
            const url = `masterGroup/` + relationshipId;
            let response: Response;

            await apiConnector.deleteData(url, buildToBeRemovedRelationships(relationship[0])).then(async (data) => {
                response = data;
            }).catch(error => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                const json = await response.json();
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
                }
                return reject({ status: response.status, message: genericError });
            });
        }
        // Unable to delete the Master Group. Couldn't find it.
        return new Promise<boolean>(async (resolve, reject) => {
            reject({ status: INTERNAL_SERVER_ERROR, message: couldNotFindRecordToDelete });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getMasterGroupRelationship = async (masterId: String, accessGroupId: String): Promise<AccessGroupMaster[]> => {
    try {
        const url = `masterGroup?include=master,accessGroup&filter=master.id==${masterId};accessGroup.id==${accessGroupId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, results: any[]) => {
                if (err) {
                    return reject(err);
                }
                const masterGroups: AccessGroupMaster[] = results.map((masterGroupRelationShip): AccessGroupMaster => {

                    return {
                        id: masterGroupRelationShip['id'],
                        dateTimeEffect: masterGroupRelationShip['mg-date-time-in-effect'],
                        endDateTimeUtc: masterGroupRelationShip['mg-end-date-time-utc'],
                        expires: masterGroupRelationShip['mg-expires'],
                        startDateTimeUtc: masterGroupRelationShip['mg-start-date-time-utc'],
                        masterId: masterGroupRelationShip['master']['id'],
                        accessGroupId: masterGroupRelationShip['access-group']['id']
                    };
                });

                return resolve(masterGroups);
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildAccessGroupData = (toBeAssigned: string[], accessTimePatternId: string, access: string) => {
    return toBeAssigned.map((accessGroupId) => {
        return ({
            data: {
                type: "accessGroup",
                id: accessGroupId,
                relationships: {
                    [access]: {
                        data: {
                            type: access,
                            id: accessTimePatternId,
                        },
                    },
                },
            },
        })
    })
};

export const assignAccessToAccessGroups = async (accessTimePatternId: string, toBeAssigned: string[], access) => {
    if (toBeAssigned.length === 0) {
        return Promise.resolve({ success: true })
    }

    const newAccessAreas = buildAccessGroupData(toBeAssigned, accessTimePatternId, access);
    const addedAccessGroups = newAccessAreas.map(async (accessGroup) => {
        const url = `accessGroup/${accessGroup.data.id}`;
        await save(url, accessGroup, false)
    });

    return Promise.allSettled([...addedAccessGroups]);

}

const save = async (url, access, create: false) => {
    try {
        let response: Response;
        await apiConnector.sendData(url, create, access).then(async (data) => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve({ success: true });
            }
            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessGroupsForAccessProfile = async (accessProfileId: string | number, translate: Translator, listParameters?: ListQuery): Promise<AccessGroupWrapper> => {
    try {
        const filtersWithAccessProfile: Filter[] = appendFilter(accessProfileId, "Access Profile", "accessProfile.id", listParameters?.filters ?? []);
        const searchOnNames: string[] = ['accessGroup.accessTimePattern.atpMon', 'accessGroup.accessTimePattern.atpTue', 'accessGroup.accessTimePattern.atpWed', 'accessGroup.accessTimePattern.atpThu', 'accessGroup.accessTimePattern.atpFri', 'accessGroup.accessTimePattern.atpSat', 'accessGroup.accessTimePattern.atpSun', 'accessGroup.accessTimePattern.atpHol'];
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, customFilterBuilder(searchOnNames, translate, filtersWithAccessProfile, listParameters?.searchTerm));
        const agpFields = 'fields[accessGroupProfile]=accessGroup';
        const agFields = 'fields[accessGroup]=agName,accessProfileNames,totalMasters,site,area,accessTimePattern,agEndDate,agMaxCapacity,agStartDate,agSuspended,agDesc,agDefault,isDefault,totalAccessProfiles';
        const atpFields = 'fields[accessTimePattern]=atpName,atpStartTime,atpDuration,atpMon,atpTue,atpWed,atpThu,atpFri,atpSat,atpSun,atpHol';
        const areaFields = 'fields[area]=areaName';
        const siteFields = 'fields[site]=siteName';
        const url = `accessGroupProfile?include=accessGroup,accessGroup.accessTimePattern,accessGroup.area,accessGroup.site${partialURL}&${agpFields}&${agFields}&${atpFields}&${areaFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta)

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, accessProfiles: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const accessGroups: AccessGroup[] = accessProfiles
                    .map((accessProfile) => {
                        // Get the access group from the deserialized data
                        const accessGroupData = accessProfile['access-group'];
                        if (!accessGroupData) {
                            console.error('No access-group found in profile:', accessProfile);
                            return undefined;
                        }

                        // Find the raw included data
                        const includedAccessGroup = json.included.find(
                            (item: any) => item.type === 'accessGroup' && item.id === accessGroupData.id
                        );

                        const accessTimePattern = json.included.find(
                            (item: any) => item.type === 'accessTimePattern' &&
                                item.id === includedAccessGroup?.relationships?.accessTimePattern?.data?.id
                        );

                        const area = json.included.find(
                            (item: any) => item.type === 'area' &&
                                item.id === includedAccessGroup?.relationships?.area?.data?.id
                        );

                        const site = json.included.find(
                            (item: any) => item.type === 'site' &&
                                item.id === includedAccessGroup?.relationships?.site?.data?.id
                        );

                        // Construct the data structure expected by buildAccessGroup
                        const constructedAccessGroup = {
                            id: accessGroupData.id,
                            'ag-name': accessGroupData['ag-name'],
                            'access-time-pattern': {
                                'atp-name': accessTimePattern?.attributes?.atpName,
                                'atp-start-time': accessTimePattern?.attributes?.atpStartTime,
                                'atp-duration': accessTimePattern?.attributes?.atpDuration,
                                'id': accessTimePattern?.id,
                                'atp-fri': accessTimePattern?.attributes?.atpFri,
                                'atp-hol': accessTimePattern?.attributes?.atpHol,
                                'atp-mon': accessTimePattern?.attributes?.atpMon,
                                'atp-sat': accessTimePattern?.attributes?.atpSat,
                                'atp-sun': accessTimePattern?.attributes?.atpSun,
                                'atp-thu': accessTimePattern?.attributes?.atpThu,
                                'atp-tue': accessTimePattern?.attributes?.atpTue,
                                'atp-wed': accessTimePattern?.attributes?.atpWed
                            },
                            'area': {
                                'area-name': area?.attributes?.areaName,
                                'id': area?.id
                            },
                            'site': {
                                'site-name': site?.attributes?.siteName,
                                'id': site?.id
                            },
                            'total-masters': accessGroupData['total-masters'],
                            'ag-end-date': accessGroupData['ag-end-date'],
                            'ag-max-capacity': accessGroupData['ag-max-capacity'],
                            'ag-start-date': accessGroupData['ag-start-date'],
                            'ag-suspended': accessGroupData['ag-suspended'],
                            'ag-desc': accessGroupData['ag-desc'],
                            'ag-default': accessGroupData['ag-default'],
                            'is-default': accessGroupData['is-default'],
                            'access-group-actions': [],
                            'total-access-profiles': accessGroupData['total-access-profiles']
                        };

                        return buildAccessGroup(constructedAccessGroup,translate, accessProfile['access-group']['access-profile-names']);
                    })
                    .filter((group): group is AccessGroup => group !== undefined);

                return resolve({ rows: accessGroups, pagination });
            })
        })

    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/**
 * This function is used to assign and unassign access groups to/from an entity which has a direct relationship with access groups 
 *
 * @param toBeAssigned -string array of access group ids to be assigned
 * @param toBeUnassigned - string array of access group ids to be unassigned
 * @param itemId - entity id we are assigning and unassigning access groups to/from
 * @param entity - entity type we are assigning and unassigning access groups to/from
 * @returns - Promise of allSettled
 */
export const assignUnassignAccessGroups = async (toBeAssigned: string[], toBeUnassigned: string[], itemId: string, entity: "area" | "accessTimePattern") => {
    //Unassign
    const removed = toBeUnassigned.map(async (id) => {
        const relationship = {
            data: {
                type: entity,
                id: itemId
            }
        }
        return await apiConnector.deleteData(`accessGroup/${id}/relationships/${entity}/${itemId}`, relationship);
    });

    //Assign
    const added = toBeAssigned.map(async (id) => {
        const relationship = {
            data: {
                type: entity,
                id: itemId
            }
        }
        return await apiConnector.sendData(`accessGroup/${id}/relationships/${entity}`, false, relationship);
    });

    return Promise.allSettled([...removed, ...added]);
}
